/**
 * 쿨링 팬 판매 페이지의 이미지 슬롯 — **교체 지점을 한 곳에 모은다.**
 *
 * ## 왜 맵인가
 *
 * 시안의 이미지 자리가 한꺼번에 채워지지 않는다(2026-10-01 기준 **12곳 전부 채움** —
 * 마지막 STEP 03 은 영상). 경로가 화면 컴포넌트에 흩어져 있으면 에셋이 도착할 때 **한 컷이
 * 조용히 빈 채로 남는다** — 화면은 그려지고 `tsc`·lint·다른 테스트가 전부 침묵한다.
 * 그래서 슬롯을 여기 한 곳에만 둔다.
 *
 * ## 시안 실측 (2026-09-29 전수 재조회 · Figma `8X5fPtMR3n9lVij5MJKaUh`)
 *
 * `30491:52700` 쿨링팬 (Desktop) 1920×6556 · `30810:38089` 쿨링팬 (Mobile) 360×7178.
 * 시안이 움직이면 **거기서 다시 뜬다**(추측 금지).
 *
 * 표의 행 수 = 아래 `COOLING_FAN_FIGURES` 의 슬롯 수다(**12**). 슬롯을 늘리면 여기도
 * 늘린다 — 차집합으로 확인한다:
 * `comm -23 <(grep -oE '^  [a-zA-Z]+: slot' … | sed 's/: slot//' | sort) <(표행 | sort)`
 *
 * ```
 * 슬롯                    노드(D)        노드(M)        시안 박스 D / M        모바일에서   상태
 * introHero              30745:38952    31190:38290    708×500 / 328×232      같은 컷 =   채움
 * introFanSpeed          31185:31702    ─              708×500                (짝 있음)   채움 · Midnight Flow
 * introFanSpeedMobile    ─              31199:38292    ─ / 328×232            =           채움
 * stepAttach             30765:38965    31190:38415    464×302 / 328×213      같은 컷 ≈   채움
 * stepConnect            30765:39046    31190:38416    464×302 / 328×213      같은 컷 ≈   채움
 * stepPlayer             30765:39058    30812:234969   464×302 / 328×213      같은 컷 ✗   채움 · 영상(10-01) · 좌우 6.7% 크롭
 * chargingFanOn          31045:38326    ─              668×400                (짝 있음)   채움
 * chargingFanOnMobile    ─              31045:38364    ─ / 320×192            =           채움
 * chargingBattery        31045:38296    ─              668×400                (짝 있음)   채움
 * chargingBatteryMobile  ─              31045:38365    ─ / 320×192            =           채움
 * specsDimensions        30995:38711    ─              887×700                (짝 있음)   채움 · 벡터 도면
 * specsDimensionsMobile  ─              31205:38289    ─ / 328×309            =           채움 · 2장 합성
 * ```
 *
 * 「모바일에서」: `=` 는 모바일 박스와 에셋 종횡비가 같음 · `≈` 는 데스크탑 컷을 모바일에도
 * 쓰는데 비율이 0.3% 이내로 같음(모바일 노드에 같은 그림의 축소 크롭이 있다 — 1.536 vs 1.540).
 * `✗` 는 에셋 비율이 박스와 크게 달라 `object-cover` 가 잘라냄(이유는 그 슬롯 docblock).
 *
 * ## ⚠️ 이 표를 고칠 때 돌리는 술어
 *
 * *"**에셋이 채워졌고**, 모바일 컷 짝이 없고, 모바일 박스 비율이 에셋과 **크게**(>5%)
 * 다른 슬롯은 그 이유를 이 행에 적는다."* 2026-10-01 기준 **1곳** — `stepPlayer`(16:9 영상을
 * 1.54 자리에 · 좌우 6.7% 크롭 · 그 슬롯 docblock 에 잘림 확인 결과).
 *
 * ## ★2026-09-23 대비 바뀐 것 (이 파일이 낡지 않게 적어 둔다)
 *
 * - 3단 풍속이 회색 카드 + 흰 칩 도식 → **「Midnight Flow」** 합성 컷(두 폭 모두 새 export).
 * - 「쉬운 탈부착」이 **모바일에서도** 빠졌다 → `introDetach` 슬롯 삭제.
 * - 모바일 사양 도면이 **Front·Back 두 장**이 됐다 → 한 파일로 합성(`specsDimensionsMobile`).
 * - 히어로·STEP 01·02·충전 4컷·데스크탑 도면은 **바이트 대조로 변경 없음**을 확인했다
 *   (export 바이트 수 / 원본 fill md5 / SVG 는 클립 id 외 동일).
 *
 * ## `introHero`·`introFanSpeed` 는 최종 촬영물이 아닐 수 있다
 *
 * 둘 다 캔버스에 얹힌 AI 목업(`ChatGPT Image 2026년 9월 14일 …`)을 프레임째 export 한 것이다.
 * 2026-09-16 에 작성자가 **쓸 에셋으로 직접 지정했다**(「이미지 채우자」). 실촬 컷이 나오면
 * 같은 경로에 덮고 `width`/`height` 만 갱신한다(가드가 불일치를 잡는다).
 *
 * ⚠️ 보드는 **세션 중에도 바뀐다**(실측: 2026-09-14 13:31→13:55 에 `introFanSpeed` 가
 * 빨강 → 그림). **한 번 뜬 스냅샷을 근거로 재사용하지 말 것** — 재개할 때 위 12개를 다시 뜬다.
 *
 * ## 촬영 컷은 이 맵에 없다
 *
 * 갤러리(상단 썸네일 레일 + 라이트박스)는 mgmt 의 `productImages` 를 그대로 쓴다
 * (`ProductImageGallery`). 코드에 경로가 없으므로 촬영이 늦어도 이 파일은 안 바뀐다.
 */

/** 정식 에셋이 들어올 디렉터리. 경로를 손으로 반복하지 않는다. */
const BASE = "/images/tactsuit/cooling-fan";

export type CoolingFanFigureSlot = {
  /** 시안 노드 id. 렌더에는 쓰지 않고 **대조용**이다. */
  node: string;
  /** 시안 프레임 크기(px). `next/image` 의 종횡비 예약과 플레이스홀더가 함께 쓴다. */
  width: number;
  height: number;
  /**
   * 정식 에셋 경로. `null` = **디자인 산출물 대기**.
   *
   * ★가드가 `null` 개수를 정확값으로 센다 — 채우면 빨간불이 나고, 그때 가드 docblock 의
   * 「교체 시 함께 할 일」을 읽게 된다.
   */
  src: string | null;
  /**
   * 이 자리가 **영상**이면 그 MP4 경로. 그때 `src` 는 **포스터**(첫 프레임 JPG)다 —
   * 재생 전·`prefers-reduced-motion` 에서 보이는 그림이고, 가드가 크기를 대조한다.
   */
  video?: string;
};

/**
 * ⚠️ **`satisfies` 가 아니라 이 헬퍼를 쓴다.** `satisfies Record<…>` 는 키를 추론해 주는
 * 대신 `src` 를 리터럴 `null` 로 좁혀 버려서, 「채워졌을 때」를 다루는 코드가 `never` 로
 * 죽는다(가드의 `filter(f => f.src !== null)`). 반환 타입을 명시한 항등 함수를 거치면
 * **키 추론은 유지하면서 `src` 가 `string | null` 로 남는다** — 경로를 채워도 타입이
 * 안 바뀐다.
 */
const slot = (s: CoolingFanFigureSlot): CoolingFanFigureSlot => s;

/**
 * 이미지 슬롯 12개. 키는 **의미**로 짓는다 — 시안의 프레임 이름(`copy`·`Cover`)은 여러
 * 자리에서 중복되고, 기획 문서의 컷 번호는 그 문서가 개정되면 통째로 어긋난다.
 *
 * ⚠️ 2026-09-23 재조회 에셋은 전부 **Figma 에서 직접 받았다**(`download_assets`). export 가
 * 노드에 설정된 포맷·배율을 따르므로 히어로·3단 풍속은 **JPEG 2x**(모바일 3단 풍속은 3x)로
 * 나온다 — 선언 `width`/`height` 는 **파일의 실제 크기**다(가드가 대조한다).
 */
export const COOLING_FAN_FIGURES = {
  /**
   * 제품 소개 1번 — 등판 기류. 2026-09-23 시안에서 **풀블리드 히어로가 사라지고** 2단
   * 카드의 왼쪽(708×500 · 다크 배경 · 텍스트는 아래)이 됐다.
   *
   * 모바일 노드(`30854:38632` 328×240)는 여전히 **빈 다크 박스**라 이 컷을 같이 쓴다.
   * 종횡비가 1.416 vs 1.367 로 거의 같아 `object-cover` 가 가로를 ~3.5% 만 자른다
   * (종전 1440×500 컷은 52.5% 를 잘라 `object-position` 으로 버텼다).
   */
  introHero: slot({
    node: "30745:38952",
    width: 1416,
    height: 1000,
    src: `${BASE}/intro-hero.jpg`,
  }),
  /**
   * 제품 소개 2번 — 3단 풍속. **데스크탑 컷.** 2026-09-29 완성본에서 회색 카드 + 흰 칩 도식이
   * **「Midnight Flow」**(다크 `#080F1C` · 블루 조명 · 리모컨 실사 · 빛의 흐름)로 바뀌었다
   * (`31185:31702` 708×500). 프레임째 export 한 합성 컷이다.
   *
   * ★제품 소개 3번 「쉬운 탈부착」(`introDetach`)은 2026-09-29 에 **모바일 보드에서도 빠졌다**
   * — 슬롯째 걷었다.
   */
  introFanSpeed: slot({
    node: "31185:31702",
    width: 1416,
    height: 1000,
    src: `${BASE}/intro-fan-speed.jpg`,
  }),
  /**
   * 같은 도식의 **모바일 컷**(`31199:38292` 328×231.638 · 「최신 데스크톱 반영」). 비율은
   * 데스크탑과 같지만(1.416) 모바일은 숫자 2·1 이 `#C7DCE7` 로 한 톤 낮아 **별도 export** 다.
   *
   * ⚠️ 이 슬롯은 **아트 디렉션**이다 — `CoolingFanFigure` 가 `<picture>` 로 고른다.
   */
  introFanSpeedMobile: slot({
    node: "31199:38292",
    width: 656,
    height: 464,
    src: `${BASE}/intro-fan-speed-mobile.jpg`,
  }),
  /**
   * 사용 방법 STEP 01 — 등판에 부착(분해도). 2026-09-23 에 커버가 464×360 → **464×302**.
   * 모바일 노드(`30812:234949` 328×220)는 빈 다크 박스라 이 컷을 같이 쓴다(1.536 vs 1.491).
   */
  stepAttach: slot({
    node: "30765:38965",
    width: 464,
    height: 302,
    src: `${BASE}/step-attach.png`,
  }),
  /** 사용 방법 STEP 02 — USB Type-C 연결. 모바일은 STEP 01 과 같은 사정. */
  stepConnect: slot({
    node: "30765:39046",
    width: 464,
    height: 302,
    src: `${BASE}/step-connect.png`,
  }),
  /**
   * 사용 방법 STEP 03 — bHaptics Player 에서 액세서리 전원을 켜는 화면 녹화(**영상**).
   * 09-29 완성본까지 유일하게 비어 있던 자리를 2026-10-01 에 작성자가 채웠다.
   *
   * 원본 MP4 1202×676 · 60fps · 7.32초 · 8.9MB → 같은 해상도·같은 프레임으로 재인코딩
   * (H.264 CRF 23 · faststart · 오디오 없음) **1.0MB**. WebM(VP9)은 60fps 에서 1.1MB 로
   * 오히려 커서 두지 않았다 — MP4 하나가 모든 브라우저를 덮는다.
   *
   * 자리 비율(464/302 · 328/213 ≈ 1.54)과 영상 비율(1.78)이 달라 `object-cover` 가 좌우를
   * 6.7% 씩 자른다. 잘라서 미리 본 결과(4fps 30프레임) 탭 → 토글 → 확인 창 → Allow → 켜짐
   * 흐름은 전부 남고, 줌인 중 1~2 프레임만 토글이 오른쪽 끝에 걸린다.
   *
   * ⚠️ 2026-09-30 에 먼저 받은 GIF(1920×1080 · 145.5MB · 256색)는 화질이 나빠 반려됐다 —
   * 에셋을 다시 받으면 **GIF 가 아니라 MP4 원본**을 받는다.
   */
  stepPlayer: slot({
    node: "30765:39058",
    width: 1202,
    height: 676,
    src: `${BASE}/step-player-poster.jpg`,
    video: `${BASE}/step-player.mp4`,
  }),
  /**
   * 충전 안내 — 쿨링 팬 사용 시(주황·파랑 LED). **데스크탑 컷**: 카드 668×400 전체를
   * 덮는 사진이고 텍스트가 왼쪽 위에 얹힌다(`31045:38326` · 원본 1336×800).
   *
   * ⚠️ 원본은 **왼쪽이 투명**하다 — 시안은 그 자리를 `#1c1c1c` 로 깔아 텍스트 배경으로
   * 쓴다. PNG(1.1MB)를 그대로 두지 않고 `#1c1c1c` 위에 합성해 JPEG 로 저장했다.
   * 에셋을 다시 받으면 **그 합성을 다시** 한다.
   *
   * ⚠️ 보드에서 이 카드들은 부모·자식이 **좌우 반전을 두 번** 겹친 구조다. 노드만 따로
   * 렌더하면 한 번 뒤집힌 채 나올 수 있어 export 가 아니라 **원본 fill** 을 받았다.
   */
  chargingFanOn: slot({
    node: "31045:38326",
    width: 1336,
    height: 800,
    src: `${BASE}/charging-fan-on.jpg`,
  }),
  /** 같은 카드의 **모바일 컷** — 이미지 위/텍스트 아래, 320×192(`31045:38364` 「cc 1」). */
  chargingFanOnMobile: slot({
    node: "31045:38364",
    width: 1336,
    height: 800,
    src: `${BASE}/charging-fan-on-mobile.jpg`,
  }),
  /** 충전 안내 — 배터리 충전 시(파랑 LED). 데스크탑 컷(`31045:38296`). 합성 처리는 위와 같다. */
  chargingBattery: slot({
    node: "31045:38296",
    width: 1336,
    height: 800,
    src: `${BASE}/charging-battery.jpg`,
  }),
  /** 같은 카드의 **모바일 컷**(`31045:38365` 「dd 1」). */
  chargingBatteryMobile: slot({
    node: "31045:38365",
    width: 1336,
    height: 800,
    src: `${BASE}/charging-battery-mobile.jpg`,
  }),
  /**
   * 제품 사양 — 치수도(Front·Back, 460×350mm 라벨 포함).
   *
   * ★종전 보드는 회색 빈 박스(`30765:45365`)였는데
   * 2026-09-16 재조회에서 그 안에 **벡터 도면**(`30995:38711` 887×700)이 들어 있었다.
   * 빨강·다크 자리표시와 달리 치수까지 적힌 완성 도면이라 내려받아 커밋했다.
   *
   * ⚠️ 원본 export 에 `preserveAspectRatio="none"` 이 박혀 있어 **박스 비율이 887/700 과
   * 다르면 도면이 찌그러진다**(모바일 자리는 328 폭이다). 커밋한 파일에서 그 속성을
   * 지웠다 — 기본값 `xMidYMid meet` 이 적용된다. 에셋을 다시 받으면 **그 처리를 다시** 한다.
   */
  specsDimensions: slot({
    node: "30995:38711",
    width: 887,
    height: 700,
    src: `${BASE}/dimensions.svg`,
  }),
  /**
   * 제품 사양 — **모바일 도면**. 2026-09-29 완성본에서 모바일은 Front·Back 두 장을 gap 20 ·
   * p2 로 붙여 328 폭을 채운다(`31205:38289` 328×308.6 · 자식 `31206:38289`·`31206:38290`).
   * 데스크탑 887×700 한 장을 줄이면 가운데 여백이 커서 도면이 작아지므로 디자이너가 따로 짰다.
   *
   * ★두 SVG 를 **한 파일로 합쳤다**(중첩 `<svg>` 2개 · id 는 `front-`/`back-` 접두어로
   * 충돌 제거 · 합성 후 id 148 중복 0 · 끊긴 `url(#…)` 참조 0). 두 장으로 두면 데스크탑 컷과
   * `<picture>` 로 고를 수 없어 폭 토글(`hidden`/`laptop:hidden`)이 되는데, 그러면 1440 에서
   * 숨은 모바일 두 장(88KB)을 **받는다**(2026-09-29 실측 · 리뷰 R2 3번). 한 장이면
   * `specsDimensions` 의 `mobileSlot` 으로 들어가 한 쪽만 받는다.
   *
   * ⚠️ 원본 export 의 `preserveAspectRatio="none"` 은 `dimensions.svg` 와 같은 이유로 지웠다.
   * 에셋을 다시 받으면 **합성과 속성 제거를 다시** 한다.
   */
  specsDimensionsMobile: slot({
    node: "31205:38289",
    width: 328,
    height: 309,
    src: `${BASE}/dimensions-mobile.svg`,
  }),
};

export type CoolingFanFigureKey = keyof typeof COOLING_FAN_FIGURES;

/**
 * 제품 사양 4칸의 값. `null` 은 **미확정**, 문자열은 **번역 키**다(화면에서 `t()` 를 거친다).
 *
 * 2026-09-29 완성본 기준 — 크기·냉각 방식·무게는 두 보드 모두 자리표시(`0000000` · `000`)다.
 * 4번 칸은 라벨이 「TactSuit Pro 전용」 → **「호환 기기」**로 바뀌었고, 모바일 보드가 그 값으로
 * 「TactSuit Pro 전용」을 적었다(`30812:235026`). 데스크탑은 아직 `0000000` 이지만 같은 칸이라
 * 모바일 값을 양쪽에 쓴다.
 *
 * ⚠️ **자리표시 문자열을 그대로 내보내지 않는다** — `null` 로 두고 화면에서 `—` 로 그린다.
 * 가드가 `null` 개수를 정확값으로 센다.
 *
 * ⚠️ 무게는 제품 소개 본문에 **462.5g** 이 있다(시안 `31154:31622`). 사양 칸의 값으로 확정된
 * 것은 아니라 **옮겨 적지 않았다** — 산문의 수치를 표의 값으로 승격하는 것은 별개의 확인이다.
 *
 * 2026-10-08 사용자 확정값으로 세 칸을 채웠다 — 크기 `460 * 350 * 58mm`(곱셈 기호만 `×` 로) ·
 * 무게 462.5g(본문 값과 같음) · 냉각 방식 Blower fan. 미확정 칸 0.
 */
export const COOLING_FAN_SPEC_VALUES: Record<
  "size" | "weight" | "cooling" | "compatibility",
  string | null
> = {
  size: "460 × 350 × 58mm",
  cooling: "Blower fan",
  weight: "462.5g",
  compatibility: "TactSuit Pro only",
};

export type CoolingFanSpecKey = keyof typeof COOLING_FAN_SPEC_VALUES;
